import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { supabase } from "@/lib/supabaseClient";
import { logActivity } from "@/lib/logActivity";
import PaymentStatusBadge from "@/components/base/PaymentStatusBadge";
import {
  calculateContractBalance,
  type ContractBalanceSummary,
} from "@/lib/contractBalance";

/* ─── Types ─────────────────────────────────────────── */
type Payment = {
  id: string;
  contract_id: string;
  client_id: string;
  tenant_id: string;
  payment_date: string;
  amount: number;
  method: string;
  reference: string;
  notes: string;
  status: string;
  client_name: string;
  contract_number: string;
  created_at: string;
};

type Contract = {
  id: string;
  tenant_id: string;
  client_id: string;
  client_name: string;
  contract_number: string;
  title: string;
  total_amount: number;
  deposit_amount: number;
  balance_amount: number;
  payment_status: string;
};

interface Props {
  contract: Contract;
  tenantId: string;
  onPaymentStatusChange: (status: string, balance: number) => void;
}

/* ─── Constants ─────────────────────────────────────── */
const INPUT =
  "w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/60 transition-colors";

const BLANK_FORM = {
  payment_date: new Date().toISOString().split("T")[0],
  amount: 0,
  method: "cash",
  reference: "",
  notes: "",
  status: "confirmed",
};

const METHOD_ICONS: Record<string, string> = {
  cash: "ri-money-dollar-circle-line",
  card: "ri-bank-card-line",
  transfer: "ri-bank-line",
  zelle: "ri-exchange-dollar-line",
  venmo: "ri-smartphone-line",
  check: "ri-file-list-3-line",
  other: "ri-more-2-line",
};

const METHOD_LABELS: Record<string, string> = {
  cash: "Cash",
  card: "Card",
  transfer: "Bank Transfer",
  zelle: "Zelle",
  venmo: "Venmo",
  check: "Check",
  other: "Other",
};

const STATUS_COLORS: Record<string, string> = {
  confirmed: "text-emerald-600",
  completed: "text-emerald-600",
  paid: "text-emerald-600",
  pending: "text-amber-500",
  refunded: "text-violet-500",
  void: "text-slate-400",
  cancelled: "text-red-400",
};

/* ─── Component ─────────────────────────────────────── */
export default function PaymentsTab({ contract, tenantId, onPaymentStatusChange }: Props) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...BLANK_FORM });
  const [saving, setSaving] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [editForm, setEditForm] = useState({ ...BLANK_FORM });
  const [balanceSummary, setBalanceSummary] = useState<ContractBalanceSummary | null>(null);

  /* ── Balance refresh ── */
  const refreshBalance = useCallback(async () => {
    const summary = await calculateContractBalance(tenantId, contract.id, { sync: true });
    if (summary) {
      setBalanceSummary(summary);
      onPaymentStatusChange(summary.paymentStatus, summary.balanceRemaining);
    }
    return summary;
  }, [tenantId, contract.id, onPaymentStatusChange]);

  /* ── Initial fetch ── */
  const fetchPayments = useCallback(async () => {
    setLoading(true);
    const [paymentsRes] = await Promise.all([
      supabase
        .from("payments")
        .select("*")
        .eq("contract_id", contract.id)
        .eq("tenant_id", tenantId)
        .order("payment_date", { ascending: false }),
    ]);
    setPayments((paymentsRes.data ?? []) as Payment[]);
    await refreshBalance();
    setLoading(false);
  }, [contract.id, tenantId, refreshBalance]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  /* ── Add payment ── */
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const { data, error } = await supabase
      .from("payments")
      .insert({
        ...form,
        contract_id: contract.id,
        client_id: contract.client_id,
        tenant_id: tenantId,
        client_name: contract.client_name,
        contract_number: contract.contract_number,
        amount: Number(form.amount),
      })
      .select()
      .maybeSingle();

    if (!error && data) {
      setPayments((prev) => [data as Payment, ...prev]);
      await refreshBalance();
      logActivity({
        tenantId,
        action: "created",
        entity: "payment",
        entityId: (data as Payment).id,
        entityName: `$${form.amount} for ${contract.contract_number}`,
      });
      setForm({ ...BLANK_FORM });
      setShowForm(false);
    }
    setSaving(false);
  };

  /* ── Save edit ── */
  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPayment) return;
    setSaving(true);

    const { data, error } = await supabase
      .from("payments")
      .update({ ...editForm, amount: Number(editForm.amount), updated_at: new Date().toISOString() })
      .eq("id", editingPayment.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();

    if (!error && data) {
      setPayments((prev) => prev.map((p) => (p.id === editingPayment.id ? (data as Payment) : p)));
      await refreshBalance();
      logActivity({
        tenantId,
        action: "updated",
        entity: "payment",
        entityId: editingPayment.id,
        entityName: `$${editForm.amount} for ${contract.contract_number}`,
      });
      setEditingPayment(null);
    }
    setSaving(false);
  };

  /* ── Void payment ── */
  const handleVoid = async (p: Payment) => {
    const result = await Swal.fire({
      title: "Void this payment?",
      html: `<p style="color:#64748b;font-size:14px;">Payment of <strong>$${Number(p.amount).toLocaleString()}</strong> will be marked as void and excluded from the contract balance.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Void",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#f59e0b",
      cancelButtonColor: "#94a3b8",
      background: "#fff",
      color: "#0f172a",
    });
    if (!result.isConfirmed) return;

    const { data, error } = await supabase
      .from("payments")
      .update({ status: "void", updated_at: new Date().toISOString() })
      .eq("id", p.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();

    if (!error && data) {
      setPayments((prev) => prev.map((x) => (x.id === p.id ? (data as Payment) : x)));
      await refreshBalance();
      logActivity({ tenantId, action: "updated", entity: "payment", entityId: p.id, entityName: `Voided $${Number(p.amount).toLocaleString()}` });
    }
  };

  /* ── Delete payment ── */
  const handleDelete = async (p: Payment) => {
    const result = await Swal.fire({
      title: "Delete payment?",
      html: `<p style="color:#64748b;font-size:14px;">Payment of <strong>$${Number(p.amount).toLocaleString()}</strong> will be permanently deleted.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
      background: "#fff",
      color: "#0f172a",
    });
    if (!result.isConfirmed) return;

    const { error } = await supabase
      .from("payments")
      .delete()
      .eq("id", p.id)
      .eq("tenant_id", tenantId);

    if (!error) {
      setPayments((prev) => prev.filter((x) => x.id !== p.id));
      await refreshBalance();
    }
  };

  const formatDate = (d: string) => {
    if (!d) return "—";
    try {
      return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch { return d; }
  };

  /* ── Render form ── */
  const renderForm = (
    f: typeof BLANK_FORM,
    setF: (fn: (p: typeof BLANK_FORM) => typeof BLANK_FORM) => void,
    onSubmit: (e: React.FormEvent) => void,
    onCancel: () => void,
    submitLabel: string,
  ) => (
    <form onSubmit={onSubmit} className="bg-slate-50 rounded-xl border border-slate-200 p-5 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-slate-500 text-xs mb-1.5 block font-semibold uppercase tracking-wider">Date *</label>
          <input
            type="date"
            value={f.payment_date}
            onChange={(e) => setF((p) => ({ ...p, payment_date: e.target.value }))}
            required
            className={INPUT}
          />
        </div>
        <div>
          <label className="text-slate-500 text-xs mb-1.5 block font-semibold uppercase tracking-wider">Amount ($) *</label>
          <input
            type="number"
            min={0.01}
            step={0.01}
            value={f.amount || ""}
            onChange={(e) => setF((p) => ({ ...p, amount: Number(e.target.value) }))}
            required
            placeholder="0.00"
            className={INPUT}
          />
        </div>
        <div>
          <label className="text-slate-500 text-xs mb-1.5 block font-semibold uppercase tracking-wider">Method</label>
          <select
            value={f.method}
            onChange={(e) => setF((p) => ({ ...p, method: e.target.value }))}
            className={INPUT + " cursor-pointer"}
          >
            {Object.entries(METHOD_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-slate-500 text-xs mb-1.5 block font-semibold uppercase tracking-wider">Status</label>
          <select
            value={f.status}
            onChange={(e) => setF((p) => ({ ...p, status: e.target.value }))}
            className={INPUT + " cursor-pointer"}
          >
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="void">Void</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="text-slate-500 text-xs mb-1.5 block font-semibold uppercase tracking-wider">Reference</label>
          <input
            type="text"
            value={f.reference}
            onChange={(e) => setF((p) => ({ ...p, reference: e.target.value }))}
            className={INPUT}
            placeholder="Check #, transaction ID…"
          />
        </div>
        <div className="col-span-2">
          <label className="text-slate-500 text-xs mb-1.5 block font-semibold uppercase tracking-wider">Notes</label>
          <input
            type="text"
            value={f.notes}
            onChange={(e) => setF((p) => ({ ...p, notes: e.target.value }))}
            className={INPUT}
            placeholder="Optional"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm text-slate-500 hover:text-[#0f172a] border border-slate-200 bg-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-1.5 px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          {saving ? <><i className="ri-loader-4-line animate-spin"></i> Saving…</> : submitLabel}
        </button>
      </div>
    </form>
  );

  /* ── Main render ── */
  return (
    <div className="space-y-5">

      {/* ── Balance Summary ── */}
      {balanceSummary ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
              <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-1">Base Total</p>
              <p className="text-[#0f172a] font-bold text-lg">${balanceSummary.baseTotal.toLocaleString()}</p>
            </div>
            {balanceSummary.addonTotal > 0 && (
              <div className="bg-[#C8A96E]/5 rounded-xl border border-[#C8A96E]/20 p-4 text-center">
                <p className="text-[#9a7840] text-[10px] uppercase tracking-wider mb-1 flex items-center justify-center gap-1">
                  <i className="ri-puzzle-line"></i> Add-ons
                </p>
                <p className="text-[#9a7840] font-bold text-lg">+${balanceSummary.addonTotal.toLocaleString()}</p>
              </div>
            )}
            <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
              <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-1">Grand Total</p>
              <p className="text-[#0f172a] font-bold text-lg">${balanceSummary.grandTotal.toLocaleString()}</p>
            </div>
            <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 text-center">
              <p className="text-emerald-600 text-[10px] uppercase tracking-wider mb-1">Paid</p>
              <p className="text-emerald-600 font-bold text-lg">${balanceSummary.totalPaid.toLocaleString()}</p>
            </div>
          </div>

          {/* Balance banner */}
          <div className={`flex items-center justify-between rounded-xl px-5 py-4 border-2 ${
            balanceSummary.balanceRemaining <= 0
              ? "bg-emerald-50 border-emerald-200"
              : "bg-amber-50 border-amber-200"
          }`}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/80">
                <i className={`text-base ${
                  balanceSummary.balanceRemaining <= 0
                    ? "ri-check-double-line text-emerald-500"
                    : "ri-wallet-3-line text-amber-500"
                }`}></i>
              </div>
              <div>
                <p className={`text-sm font-semibold ${
                  balanceSummary.balanceRemaining <= 0 ? "text-emerald-700" : "text-amber-700"
                }`}>
                  {balanceSummary.balanceRemaining <= 0 ? "Fully paid" : "Balance remaining"}
                </p>
                <p className="text-xs text-slate-500">
                  ${balanceSummary.grandTotal.toLocaleString()} total − ${balanceSummary.totalPaid.toLocaleString()} paid
                </p>
              </div>
            </div>
            <p className={`text-2xl font-bold ${
              balanceSummary.balanceRemaining <= 0 ? "text-emerald-600" : "text-amber-500"
            }`}>
              ${balanceSummary.balanceRemaining.toLocaleString()}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      )}

      {/* ── Header + Add button ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-[#0f172a] font-semibold text-sm">Payment History</h3>
          <PaymentStatusBadge status={contract.payment_status} />
        </div>
        {!showForm && !editingPayment && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 text-sm bg-[#C8A96E] hover:bg-[#b8965a] text-white font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Add Payment
          </button>
        )}
      </div>

      {/* ── Add form ── */}
      {showForm && renderForm(
        form,
        setForm as (fn: (p: typeof BLANK_FORM) => typeof BLANK_FORM) => void,
        handleAdd,
        () => { setShowForm(false); setForm({ ...BLANK_FORM }); },
        "Record Payment",
      )}

      {/* ── Payments list ── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : payments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 gap-3">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-slate-50">
              <i className="ri-money-dollar-circle-line text-slate-300 text-xl"></i>
            </div>
            <p className="text-slate-400 text-sm">No payments recorded yet</p>
            {!showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="text-xs text-[#C8A96E] hover:underline cursor-pointer"
              >
                Add the first payment →
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {payments.map((p) => {
              const isVoided = p.status === "void" || p.status === "cancelled";
              const isEditing = editingPayment?.id === p.id;

              return (
                <div key={p.id}>
                  {/* Edit form inline */}
                  {isEditing ? (
                    <div className="px-5 py-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-[#0f172a] text-sm font-semibold flex items-center gap-2">
                          <i className="ri-pencil-line text-[#C8A96E]"></i>
                          Editing payment
                        </p>
                        <button
                          onClick={() => setEditingPayment(null)}
                          className="text-slate-400 hover:text-[#0f172a] cursor-pointer"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                      {renderForm(
                        editForm,
                        setEditForm as (fn: (p: typeof BLANK_FORM) => typeof BLANK_FORM) => void,
                        handleSaveEdit,
                        () => setEditingPayment(null),
                        "Save Changes",
                      )}
                    </div>
                  ) : (
                    <div className={`flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50/80 transition-colors group ${isVoided ? "opacity-50" : ""}`}>
                      {/* Method icon */}
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-50 flex-shrink-0">
                        <i className={`${METHOD_ICONS[p.method] ?? "ri-money-dollar-circle-line"} text-[#C8A96E] text-sm`}></i>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`font-bold text-sm ${isVoided ? "line-through text-slate-400" : "text-[#0f172a]"}`}>
                            ${Number(p.amount).toLocaleString()}
                          </p>
                          <span className="text-slate-300 text-xs">·</span>
                          <p className="text-slate-500 text-xs">{METHOD_LABELS[p.method] ?? p.method}</p>
                          {p.reference && (
                            <>
                              <span className="text-slate-300 text-xs">·</span>
                              <p className="text-slate-400 text-xs font-mono">#{p.reference}</p>
                            </>
                          )}
                        </div>
                        {p.notes && (
                          <p className="text-slate-400 text-xs mt-0.5 truncate">{p.notes}</p>
                        )}
                      </div>

                      {/* Date + status */}
                      <div className="text-right flex-shrink-0 mr-2">
                        <p className="text-slate-500 text-xs">{formatDate(p.payment_date)}</p>
                        <p className={`text-xs font-semibold mt-0.5 ${STATUS_COLORS[p.status] ?? "text-slate-400"}`}>
                          {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <button
                          onClick={() => {
                            setEditingPayment(p);
                            setEditForm({
                              payment_date: p.payment_date,
                              amount: p.amount,
                              method: p.method,
                              reference: p.reference || "",
                              notes: p.notes || "",
                              status: p.status,
                            });
                            setShowForm(false);
                          }}
                          className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                          title="Edit"
                        >
                          <i className="ri-pencil-line text-xs"></i>
                        </button>
                        {!isVoided && (
                          <button
                            onClick={() => handleVoid(p)}
                            className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-all cursor-pointer"
                            title="Void payment"
                          >
                            <i className="ri-forbid-line text-xs"></i>
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(p)}
                          className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line text-xs"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}